import {map, indexOf, truncate, get} from 'lodash-es';
import moment from 'moment';

import APIFetcher from '../APIFetcher';


class ReportingApi {
  /*
  Interact with the Newsela Reporting service, which provides an interface
  for aggregating and grouping data instead of working with single objects
  at a time. See https://github.com/newsela/newsela-reporting/.
  */

  static getReport(reportParameters, options = {}) {
    /*
    Usage:

    ReportingApi.getReport({
      measures: ['quiz_score', 'count:annotations'],
      dimensions: ['student_id'],
      filters: {
        student_id: [1, 2, 3],
        classroom_id: 1,
        'header_id:ne': 4
        'date_completed:gt': '2016-07-01'
        'date_completed:le': '2016-08-01'
      }
    });
    */

    // Send the query to the reporting service.
    return APIFetcher.get(ReportingApi.buildUrl(reportParameters), options);
  }

  static buildUrl(reportParameters) {
    const parameters = ReportingApi.buildUrlSearchParamsFromObject(reportParameters);
    return `reporting/?${parameters}`;
  }

  static buildCsvDownloadUrl(reportType, reportParameters) {
    const parameters = ReportingApi.buildUrlSearchParamsFromObject(reportParameters);
    const filename = ReportingApi.buildFilenameFromObject(reportType, reportParameters);
    parameters.append('type', reportType);
    parameters.append('filename', filename);
    parameters.append('format', 'csv');
    return `/api/v2/reporting/?${parameters}`;
  }

  static buildUrlSearchParamsFromObject(reportParameters) {
    let parameters = null;

    if (typeof reportParameters === 'string') {
      // Allow the query to be specified precisely in case the parameters
      // below cannot express it.
      parameters = new URLSearchParams(reportParameters);
    } else {
      // Assume the report parameters have been defined with an object.
      parameters = new URLSearchParams();
      const defaultParameters = {
        measures: [],
        dimensions: [],
        filters: {}
      };
      reportParameters = {...defaultParameters, ...reportParameters};

      if (reportParameters.measures.length) {
        // Measures are comma-separated.
        parameters.append('measures', reportParameters.measures.join(','));
      }
      if (reportParameters.dimensions.length) {
        // Dimensions are comma-separated.
        parameters.append('dimensions', reportParameters.dimensions.join(','));
      }

      // Filter values are comma-separated to represent a logical OR.
      for (const filter of Object.keys(reportParameters.filters)) {
        let values = reportParameters.filters[filter];
        if (values === undefined) {
          // Drop the parameter. Undefined mean 'don't filter on this'.
          continue;
        }

        if (!Array.isArray(values)) {
          values = [values];
        }

        // Convert any date values to the expected string format.
        values = map(values, (value) => {
          if (moment.isDate(value) || moment.isMoment(value)) {
            return moment(value).toISOString();
          } else {
            return value;
          }
        });

        parameters.append(filter, values.join(','));
      }
    }
    return parameters;
  }

  static buildFilenameFromObject(reportType, reportParameters) {
    let filename = null;
    const args = {};
    if (reportType === 'student_work') {
      args['type'] = 'students';
    } else if (reportType === 'work') {
      args['type'] = 'assignments';
    }
    if (typeof reportParameters !== 'string') {
      const filenameFilters = [
        'date_assigned:ge', 'date_assigned:le',
        'assignment_classroom_id', 'membership_classroom_id'];
      // Get values of filters relevant to the filename.
      for (const filter of Object.keys(reportParameters.filters)) {
        if (indexOf(filenameFilters, filter) > -1) {
          if (filter === 'date_assigned:ge') {
            args['start'] = truncate(reportParameters.filters[filter], {length: 10, omission: ''});
          } else if (filter === 'date_assigned:le') {
            args['end'] = truncate(reportParameters.filters[filter], {length: 10, omission: ''});
          } else if (filter === 'assignment_classroom_id' || filter === 'membership_classroom_id') {
            let values = reportParameters.filters[filter];
            if (!Array.isArray(values)) {
              values = [values];
            }
            args['classrooms'] = values.join('-');
          }
        }
      }
      // Compose the filename based on relevant filters.
      const filename_args = ['type', 'classrooms', 'start', 'end'];
      for (const arg of filename_args) {
        const value = get(args, arg, null);
        if (value) {
          filename = filename ? [filename, value].join('_') : value;
        }
      }
    }
    filename = (filename === '') ? 'data.csv' : (filename + '.csv');
    return filename;
  }
}

export default ReportingApi;
